/* eslint-disable react-hooks/exhaustive-deps */
import {
  Alert,
  AlertCloseButton,
  Box,
  Button,
  Collapse,
  Flex,
  Icon,
  Text,
} from '@tonic-one/react';
import { Light2OIcon, StarIcon } from '@tonic-one/react-icons';
import { useToggle } from '@tonic-one/react-hooks';
import { runIfFn } from '@tonic-one/utils';
import React, { forwardRef, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GlobalApi } from '@Apis';
import { reportPendo } from '@Utils/setupPendo';
import { isEUOrUKSite, isInUIC } from '@Utils/common';
import { BannerType } from '../../../utils/enums';
import { useLayoutStore } from '@Models/useLayoutModel';
import UpgradeModal from './UpgradeModal';
import {
  checkForShowUpgradeModal,
  getBannerTypeFromData,
  gotoConnectToV1,
  gotoSolutionUpgrade,
  gotoV1ForEveryone,
  isPhaseOneModal,
  isPhaseThreeModal,
  PromotionPopupWindowPhaseEnum,
} from './shared';
import { useGlobalSettingStore } from '../../../models/useGlobalSettingModel';
import CSpinner from '@Components/CSpinner';
import PhaseOneUpgradeModal from '@Components/Banner/UpgradeBanner/PhaseOneUpgradeModal';

const CollapseToggle = ({
  children,
}: {
  children:
    | React.ReactNode
    | (({
        isOpen,
        onOpen,
        onClose,
      }: {
        isOpen: boolean;
        onOpen: () => void;
        onClose: () => void;
      }) => React.ReactNode);
}) => {
  const [isOpen, toggleIsOpen] = useToggle(true);
  const onOpen = () => toggleIsOpen(true);
  const onClose = () => {
    toggleIsOpen(false);
  };
  return (
    <Collapse in={isOpen} unmountOnExit>
      {runIfFn(children, { isOpen, onOpen, onClose })}
    </Collapse>
  );
};

const AlertSolidActionButton = forwardRef((props: any, ref) => (
  <Button
    ref={ref}
    size="sm"
    variant="secondary"
    sx={{
      borderColor: 'black:primary',
      color: 'black:primary',
      ':active': {
        color: 'black:primary',
      },
      ':focus': {
        color: 'black:primary',
      },
      ':hover': {
        background: 'rgba(0, 0, 0, 0.12)',
        color: 'black:primary',
      },
      ':hover:not(:focus)': {
        boxShadow: 'none',
      },
    }}
    {...props}
  />
));
AlertSolidActionButton.displayName = 'AlertSolidActionButton';

const ConnectedBanner = ({ closeBanner }: { closeBanner: () => void }) => {
  const { t } = useTranslation();
  return (
    <CollapseToggle>
      {({ onClose }) => (
        <Alert
          variant="solid"
          severity="info"
          icon={<Icon color="white:emphasis" as={Light2OIcon} />}
          onClose={() => {
            onClose();
            closeBanner();
          }}
          sx={{
            background: 'linear-gradient(90deg, #8f41e9 0%, #578aef 100%)',
            color: 'white:emphasis',
          }}
        >
          <Flex
            alignItems="center"
            justifyContent="space-between"
            columnGap="4x"
            mr="10x"
          >
            <Text>{t('header.banner.connectedSolutions.description')}</Text>
            <AlertSolidActionButton
              my="-1x"
              onClick={() => {
                reportPendo('ConnectedToVisionOne', {});
                gotoConnectToV1();
              }}
            >
              {t('header.banner.connectedSolutions.button')}
            </AlertSolidActionButton>
          </Flex>
          <AlertCloseButton
            top={3}
            right={7}
            position="absolute"
            data-test="alert-close-button"
          />
        </Alert>
      )}
    </CollapseToggle>
  );
};

const UpdateSolutionBanner = ({ closeBanner }: { closeBanner: () => void }) => {
  const { t } = useTranslation();
  return (
    <CollapseToggle>
      {({ onClose }: any) => (
        <Alert
          variant="solid"
          severity="info"
          icon={<Icon color="white:emphasis" as={Light2OIcon} />}
          onClose={() => {
            onClose();
            closeBanner();
          }}
          sx={{
            background: 'linear-gradient(90deg, #8f41e9 0%, #578aef 100%)',
            color: 'white:emphasis',
          }}
        >
          <Flex
            alignItems="center"
            justifyContent="space-between"
            columnGap="4x"
            mr="10x"
          >
            <Text>{t('header.banner.solutionUpgrade.description')}</Text>
            <AlertSolidActionButton
              my="-1x"
              onClick={() => {
                reportPendo('UpdateCas', {});
                gotoSolutionUpgrade();
              }}
            >
              {t('header.banner.solutionUpgrade.button')}
            </AlertSolidActionButton>
          </Flex>
          <AlertCloseButton
            top={3}
            right={7}
            position="absolute"
            data-test="alert-close-button"
          />
        </Alert>
      )}
    </CollapseToggle>
  );
};

const FlywheelBanner = ({ closeBanner }: { closeBanner: () => void }) => {
  const { t } = useTranslation();
  return (
    <CollapseToggle>
      {({ onClose }: any) => (
        <Alert
          variant="solid"
          severity="info"
          icon={<Icon color="white:emphasis" as={StarIcon} />}
          onClose={() => {
            onClose();
            closeBanner();
          }}
          sx={{
            background: 'linear-gradient(90deg, #8f41e9 0%, #578aef 100%)',
            color: 'white:emphasis',
          }}
        >
          <Flex
            alignItems="center"
            justifyContent="space-between"
            columnGap="4x"
            mr="10x"
          >
            <Text>
              {!isEUOrUKSite()
                ? t('header.banner.flywheel.not.available.description')
                : t('header.banner.flywheel.description')}
            </Text>
            <AlertSolidActionButton
              my="-1x"
              onClick={() => {
                reportPendo('Flywheel', {});
                gotoV1ForEveryone();
              }}
            >
              {!isEUOrUKSite()
                ? t('header.banner.flywheel.second.button')
                : t('header.banner.flywheel.button')}
            </AlertSolidActionButton>
          </Flex>
          <AlertCloseButton
            top={3}
            right={7}
            position="absolute"
            data-test="alert-close-button"
          />
        </Alert>
      )}
    </CollapseToggle>
  );
};

const Banner = () => {
  const { selectedOrgGlobalSetting } = useGlobalSettingStore();
  const [bannerType, setBannerType] = useState<BannerType | null>(null);
  const [loading, setLoading] = useState(false);
  const { setLayoutBanner } = useLayoutStore();

  const onCloseBanner = useCallback(() => {
    setBannerType(null);
    sessionStorage.setItem('casIsShowBanner', 'false');
  }, []);

  useEffect(() => {
    const isNeedLoadBannerApi =
      (sessionStorage.getItem('casIsShowBanner') === null ||
      sessionStorage.getItem('casIsShowBanner') === undefined
        ? true
        : sessionStorage.getItem('casIsShowBanner') === 'true') && !isInUIC();
    if (isNeedLoadBannerApi) {
      setLoading(true);
      GlobalApi.getPromotionBanner()
        .then((res) => {
          if (!res || !res.data || !res.data.name) {
            window.dispatchEvent(new CustomEvent('showWhatsNewModal'));
            return;
          }

          const bannerType = getBannerTypeFromData(res.data);
          setBannerType(bannerType);
          window.dispatchEvent(new CustomEvent('showWhatsNewModal'));
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, []);

  useEffect(() => {
    setLayoutBanner({ visible: !!bannerType, height: 40 });
  }, [bannerType, setLayoutBanner]);

  const showPromotionPopupWindow =
    bannerType &&
    checkForShowUpgradeModal() &&
    Number(selectedOrgGlobalSetting.showPromotionPopupWindow) > 0;

  const promotionPopupWindowPhase: PromotionPopupWindowPhaseEnum | null =
    selectedOrgGlobalSetting.showPromotionPopupWindow
      ? (selectedOrgGlobalSetting.showPromotionPopupWindow as PromotionPopupWindowPhaseEnum)
      : null;

  const showV1ForEveryonePopup =
    !bannerType &&
    checkForShowUpgradeModal() &&
    Number(selectedOrgGlobalSetting.showPromotionPopupWindow) > 0;

  const popUpWindow = useCallback(() => {
    if (promotionPopupWindowPhase === null) {
      return null;
    }
    if (!showPromotionPopupWindow && !showV1ForEveryonePopup) {
      return null;
    }

    let banner = bannerType || BannerType.V1ForEveryone;
    if (showV1ForEveryonePopup) {
      banner = BannerType.V1ForEveryone;
    }

    // Use helper functions to determine which modal to show
    if (isPhaseThreeModal(promotionPopupWindowPhase)) {
      return <UpgradeModal bannerType={banner} />;
    } else if (isPhaseOneModal(promotionPopupWindowPhase)) {
      return (
        <PhaseOneUpgradeModal
          bannerType={banner}
          phase={promotionPopupWindowPhase}
        />
      );
    }

    return null;
  }, [
    bannerType,
    showPromotionPopupWindow,
    showV1ForEveryonePopup,
    promotionPopupWindowPhase,
  ]);

  return (
    <Box>
      {bannerType === BannerType.ConnectedSolution && (
        <ConnectedBanner closeBanner={onCloseBanner} />
      )}
      {bannerType === BannerType.SolutionUpgrade && (
        <UpdateSolutionBanner closeBanner={onCloseBanner} />
      )}
      {bannerType === BannerType.Flywheel && (
        <FlywheelBanner closeBanner={onCloseBanner} />
      )}

      {loading ? <CSpinner /> : <>{popUpWindow()}</>}
    </Box>
  );
};

export default Banner;
